import { useCallback, useMemo, useRef, useState } from 'react';

import {
	EMPTY_IDM_FORM_ERRORS,
	type IdmApiError,
	type IdmErrorMessageKeys,
	type IdmFormErrorState,
	joinApiMessages,
	mapIdmApiError,
} from '@login/helpers/idm-errors';

type Translate = (
	key: string,
	values?: Record<string, string>,
	fallback?: string
) => string;

type UseIdmFormErrorsOptions = {
	t: Translate;
	messageKeys?: IdmErrorMessageKeys;
};

/**
 * Owns the API-side error state of an account form: the page-level banner and
 * the per-field messages IDM returns under PascalCase keys.
 *
 * Replaces the hand-rolled `apiErrorMessage` state plus `renderErrorBlock`
 * callback that was duplicated across the register-style forms, where the
 * `useCallback` was immediately invoked and so never memoised anything.
 */
export const useIdmFormErrors = ({
	t,
	messageKeys,
}: UseIdmFormErrorsOptions) => {
	const [state, setState] = useState<IdmFormErrorState>(
		EMPTY_IDM_FORM_ERRORS
	);

	// Read through a ref so callers can pass an inline object literal without
	// making `setFromApiError` change identity on every render.
	const messageKeysRef = useRef(messageKeys);
	messageKeysRef.current = messageKeys;

	const setFromApiError = useCallback(
		(error: IdmApiError) =>
			setState(mapIdmApiError(error, t, messageKeysRef.current)),
		[t]
	);

	const setBanner = useCallback(
		(banner: string) => setState({ banner, fields: {} }),
		[]
	);

	const clear = useCallback(() => setState(EMPTY_IDM_FORM_ERRORS), []);

	/**
	 * Resolves the message shown under a single field: the client-side
	 * react-hook-form message wins, then the API message for `apiKey`.
	 */
	const getFieldError = useCallback(
		(formMessage?: string, apiKey?: string) =>
			formMessage ||
			(apiKey ? joinApiMessages(state.fields[apiKey]) : ''),
		[state.fields]
	);

	return useMemo(
		() => ({
			banner: state.banner,
			fields: state.fields,
			getFieldError,
			setFromApiError,
			setBanner,
			clear,
		}),
		[
			state.banner,
			state.fields,
			getFieldError,
			setFromApiError,
			setBanner,
			clear,
		]
	);
};

export default useIdmFormErrors;
